/**
 * Admin UI: Season = event_tournament (campaign); Phase = seasons row.
 * Default phase: live → registration-open → last completed.
 */

function toTime(v) {
  if (v == null || v === '') return NaN;
  const t = new Date(v).getTime();
  return Number.isNaN(t) ? NaN : t;
}

/** Normalize admin seasons API payload to an array of phase rows. */
export function normalizeAdminPhasesList(apiData) {
  if (!apiData) return [];
  if (Array.isArray(apiData)) return apiData;
  if (Array.isArray(apiData.seasons)) return apiData.seasons;
  if (Array.isArray(apiData.data)) return apiData.data;
  return [];
}

export function isPhaseLive(phase, nowMs = Date.now()) {
  if (!phase) return false;
  const status = String(phase.status || '').toLowerCase();
  if (status === 'completed' || status === 'draft') return false;
  if (status === 'active') return true;
  const start = toTime(phase.start_date);
  const end = toTime(phase.end_date);
  if (!Number.isNaN(start) && !Number.isNaN(end) && start <= nowMs && nowMs <= end) return true;
  return false;
}

export function isPhaseRegistrationOpen(phase, nowMs = Date.now()) {
  if (!phase) return false;
  const status = String(phase.status || '').toLowerCase();
  if (status === 'completed' || status === 'draft') return false;
  const rs = toTime(phase.registration_start);
  const re = toTime(phase.registration_end);
  if (!Number.isNaN(rs) && !Number.isNaN(re) && rs <= nowMs && nowMs <= re) return true;
  return false;
}

/**
 * @returns {object|null} phase row
 */
export function pickDefaultAdminPhase(phases, nowMs = Date.now()) {
  const list = Array.isArray(phases) ? phases.filter(Boolean) : [];
  if (!list.length) return null;

  const live = list
    .filter((p) => isPhaseLive(p, nowMs))
    .sort((a, b) => toTime(b.start_date) - toTime(a.start_date));
  if (live.length) return live[0];

  const regOpen = list
    .filter((p) => isPhaseRegistrationOpen(p, nowMs))
    .sort((a, b) => toTime(b.registration_start) - toTime(a.registration_start));
  if (regOpen.length) return regOpen[0];

  const completed = list
    .filter((p) => String(p.status || '').toLowerCase() === 'completed')
    .sort((a, b) => {
      const ae = toTime(a.end_date);
      const be = toTime(b.end_date);
      if (!Number.isNaN(be) && !Number.isNaN(ae) && be !== ae) return be - ae;
      return toTime(b.start_date) - toTime(a.start_date);
    });
  if (completed.length) return completed[0];

  return [...list].sort((a, b) => toTime(b.start_date) - toTime(a.start_date))[0] || null;
}

/** Unique Season (campaign) options derived from phase rows with linked_campaign_*. */
export function buildSeasonOptionsFromPhases(phases) {
  const map = new Map();
  for (const p of phases || []) {
    const id = p.linked_campaign_id || p.linkedCampaignId;
    if (!id) continue;
    const title =
      p.linked_campaign_title || p.linkedCampaignTitle || p.linked_campaign_name || 'Season';
    if (!map.has(String(id))) {
      map.set(String(id), { id: String(id), title: String(title) });
    }
  }
  return [...map.values()].sort((a, b) => a.title.localeCompare(b.title));
}

export function filterPhasesForSeason(phases, campaignId) {
  const list = phases || [];
  if (!campaignId) return list;
  return list.filter(
    (p) => String(p.linked_campaign_id || p.linkedCampaignId || '') === String(campaignId)
  );
}

export function phaseParentSeasonId(phase) {
  if (!phase) return '';
  const id = phase.linked_campaign_id || phase.linkedCampaignId;
  return id ? String(id) : '';
}

export function resolveDefaultSeasonPhaseIds(phases, preferredPhaseId = '') {
  const list = normalizeAdminPhasesList(phases);
  if (preferredPhaseId) {
    const hit = list.find((p) => String(p.id) === String(preferredPhaseId));
    if (hit) {
      return {
        campaignId: phaseParentSeasonId(hit),
        seasonId: String(hit.id),
        phase: hit,
      };
    }
  }
  const phase = pickDefaultAdminPhase(list);
  if (!phase) return { campaignId: '', seasonId: '', phase: null };
  return {
    campaignId: phaseParentSeasonId(phase),
    seasonId: String(phase.id),
    phase,
  };
}
